import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import videojs from 'video.js'
import type VideoJsPlayer from 'video.js/dist/types/player'
import 'video.js/dist/video-js.css'

interface VideoPlayerProps {
  src: string
  className?: string
  onVideoLoaded?: (width: number, height: number) => void
  onClick?: (e: Event) => void
}

export interface VideoPlayerRef {
  player: VideoJsPlayer | null
}

const getMimeType = (url: string): string => {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'mp4':
      return 'video/mp4'
    case 'webm':
      return 'video/webm'
    case 'ogg':
      return 'video/ogg'
    case 'm3u8':
      return 'application/x-mpegURL'
    case 'mpd':
      return 'application/dash+xml'
    default:
      return 'video/mp4'
  }
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  ({ src, className, onVideoLoaded, onClick }, ref) => {
    const videoNode = useRef<HTMLVideoElement | null>(null)
    const playerRef = useRef<VideoJsPlayer | null>(null)

    useImperativeHandle(ref, () => ({
      player: playerRef.current,
    }))

    useEffect(() => {
      if (videoNode.current && !playerRef.current) {
        const options = {
          controls: true,
          responsive: true,
          fluid: false,
          sources: [
            {
              src,
              type: getMimeType(src),
            },
          ],
        }

        playerRef.current = videojs(videoNode.current, options)

        const handleEvent = (e: Event) => {
          onClick?.(e)
          e.stopPropagation()
          e.stopImmediatePropagation()
        }

        const videoWrapper = videoNode.current?.parentElement

        videoWrapper?.addEventListener('pointerdown', handleEvent, true)
        videoWrapper?.addEventListener('mousedown', handleEvent, true)
        videoWrapper?.addEventListener('touchstart', handleEvent, true)

        return () => {
          playerRef.current?.dispose()
          playerRef.current = null

          videoWrapper?.removeEventListener('pointerdown', handleEvent, true)
          videoWrapper?.removeEventListener('mousedown', handleEvent, true)
          videoWrapper?.removeEventListener('touchstart', handleEvent, true)
        }
      }
    }, [])

    useEffect(() => {
      if (playerRef.current) {
        const currentTime = playerRef.current.currentTime()
        const wasPlaying =
          !playerRef.current.paused() && !playerRef.current.ended()
        playerRef.current.src({
          src,
          type: getMimeType(src),
        })
        if (currentTime) {
          playerRef.current.currentTime(currentTime)
        }
        if (wasPlaying) {
          playerRef.current.play()?.catch(() => {
            console.warn('Autoplay prevented after refreshing video URL')
          })
        }
      }
    }, [src])

    useEffect(() => {
      const videoEl = videoNode.current

      if (!videoEl) return

      const handleLoadedMetadata = () => {
        const width = videoEl.videoWidth
        const height = videoEl.videoHeight
        onVideoLoaded?.(width, height)
      }

      videoEl.addEventListener('loadedmetadata', handleLoadedMetadata)

      return () => {
        videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata)
      }
    }, [src, onVideoLoaded])

    return (
      <div data-vjs-player className={className}>
        <video
          ref={videoNode}
          className="video-js vjs-default-skin"
          style={{
            height: '100%',
            width: '100%',
            cursor: 'pointer',
          }}
        />
      </div>
    )
  },
)

VideoPlayer.displayName = 'VideoPlayer'
